<script setup lang="ts">
import { reactive, ref, computed, onMounted } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import router from "../../router";
import axios from "axios";
import { ElMessage } from "element-plus";
import { useAuthStore } from "../../store/authStore";
import { isCompanyAdminFromToken } from "../../utils/authRole";

const store = useAuthStore();
const token = localStorage.getItem("_panda");
axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

const ensureAdminAccess = () => {
  if (!isCompanyAdminFromToken(localStorage.getItem("_panda"))) {
    router.replace("/request");
    return false;
  }
  return true;
};

interface RuleForm {
  name: string;
  email: string;
  phone: string;
  user_type: string;
  // user_status: string;
}

// Interface for edited user to include user_id
interface EditedUser {
  user_name: string;
  user_phone: string;
  user_email: string;
  user_type: string;
  user_status: string;
  user_id?: number | string;
}

// Helper function to normalize user_type to canonical string value
const normalizeUserType = (userType: string | number | undefined): string => {
  if (!userType) return "1";
  if (userType === "Admin" || userType === "2" || userType === 2) return "2";
  if (userType === "User" || userType === "1" || userType === 1) return "1";
  return String(userType);
};

// Helper function to check if user type is Admin
const isAdminType = (userType: string | number | undefined): boolean => {
  return normalizeUserType(userType) === "2";
};

const formSize = ref<"default" | "small" | "large">("default");
const formError = ref("");
const results = ref();
const loading = ref(false);
const ruleFormRef = ref<FormInstance>();
const ruleForm = reactive<RuleForm>({
  name: "",
  phone: "",
  email: "",
  user_type: "",
  // user_status: "",
});

/** Company user list for sole-admin check (same shape as Users.vue after mapping). */
const companyUsers = ref<{ user_id: number; user_type: string | number }[]>([]);

const loadCompanyUsers = async () => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_ONBOARDING_SERVICE}getusers?token=${store.getToken}`,
    );
    if (response.status === 200 && Array.isArray(response.data)) {
      companyUsers.value = response.data.map(
        (el: { user_type: number; user_id: number }) => ({
          ...el,
          user_type: el.user_type == 1 ? "Normal" : "Admin",
        }),
      );
    }
  } catch {
    companyUsers.value = [];
  }
};

onMounted(() => {
  if (!ensureAdminAccess()) return;
  void loadCompanyUsers();
});

const rules = reactive<FormRules<RuleForm>>({
  name: [
    {
      required: true,
      message: "Please enter the name of the user",
      trigger: "blur",
    },
  ],
  phone: [
    {
      required: true,
      message: "Please enter the user phone number",
      trigger: "blur",
    },
  ],
  email: [
    {
      required: true,
      message: "Please enter the user email address",
      trigger: "blur",
    },
  ],
  user_type: [
    { required: true, message: "Please enter the user type", trigger: "blur" },
  ],
  // user_status: [
  //   {
  //     required: true,
  //     message: "Please select the user status",
  //     trigger: "blur",
  //   },
  // ],
});

// Current logged-in user's admin rights — JWT-based (single source of truth)
const isCurrentUserAdmin = computed(() =>
  isCompanyAdminFromToken(localStorage.getItem("_panda")),
);

// Detect if the role is being changed
const isRoleChange = computed(() => {
  const currentEditedUser = (store.getCurrentEditedUser || {}) as EditedUser;
  const normalizedFormType = normalizeUserType(ruleForm.user_type);
  const normalizedEditedType = normalizeUserType(currentEditedUser.user_type);
  return normalizedFormType !== normalizedEditedType;
});

// Prevent modifying your own role
const isSelfRoleModification = computed(() => {
  const currentUser = store.getUserData || {};
  const currentEditedUser = (store.getCurrentEditedUser || {}) as EditedUser;
  const currentUserId = currentUser.user_id;
  const editedUserId = currentEditedUser.user_id;
  return (
    currentUserId &&
    editedUserId &&
    String(currentUserId) === String(editedUserId) &&
    isRoleChange.value
  );
});

// Sole-admin guard using the loaded company user list
const isOnlyAdmin = computed(() => {
  const adminUsers = companyUsers.value.filter(
    (u) => u.user_type === "Admin" || u.user_type === 2 || u.user_type === "2",
  );
  return (
    ruleForm.user_type !== "2" &&
    store.getCurrentEditedUser.user_type == "Admin" &&
    adminUsers.length === 1 &&
    store.getCurrentEditedUser.user_id === adminUsers[0].user_id
  );
});

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  await formEl.validate((valid, fields) => {
    if (valid) {
      // Role restriction: only Admin users can modify roles
      if (isRoleChange.value && !isCurrentUserAdmin.value) {
        formError.value = "Only Admin users can modify user roles.";
        return;
      }

      // Prevent modifying your own role
      if (isSelfRoleModification.value) {
        formError.value = "You cannot modify your own role.";
        return;
      }

      // Prevent demoting/deleting the only admin
      if (isOnlyAdmin.value) {
        formError.value =
          "You cannot demote or delete the only admin user. Please assign another admin first.";
        return;
      }

      loading.value = true;
      postData();
    } else {
      formError.value = "Something went wrong please try again.";
      console.log("error submit!", fields);
      console.log(ruleForm);
    }
  });
};

const postData = async () => {
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_ONBOARDING_SERVICE}updateuser?token=${store.getToken}`,
      {
        email: ruleForm.email,
        name: ruleForm.name,
        phone: ruleForm.phone,
        user_type: Number(ruleForm.user_type),
      },
    );

    // Handle the API response here
    loading.value = false;

    if (response.status == 200) {
      if (response.data.status) {
        // Check if current user's own role was changed
        const currentUser = store.getUserData || {};
        const currentEditedUser = (store.getCurrentEditedUser || {}) as EditedUser;
        const isCurrentUser = currentUser.user_id && currentEditedUser.user_id && 
                             String(currentUser.user_id) === String(currentEditedUser.user_id);
        const wasRoleChange = isRoleChange.value;
        
        if (isCurrentUser && wasRoleChange) {
          ElMessage({
            message: "Your role has been updated. You will be logged out to apply new permissions.",
            type: "warning",
            duration: 3000,
          });
          // Force logout after role change for current user
          setTimeout(() => {
            logwat();
          }, 2000);
          return; // Don't navigate to users page
        } else {
          ElMessage({
            message: "The user record has been saved",
            type: "success",
          });
        }
        router.push("/users");
      } else {
        ElMessage({
          message:
            "Your session has expired. Kindy login to continue enjoying our services",
          type: "error",
        });
        logwat();
      }
    } else {
      formError.value = "User update failed, please retry.";
    }

    console.log("Response:", response.data);
  } catch (error) {
    // Handle any errors
    loading.value = false;
    formError.value = "Something went wrong please try again.";
    console.error("Error:", error);
  }
};

const logwat = () => {
  store.setAuthStatus(false);
  store.$reset();
  localStorage.removeItem("_panda");
  router.push("/login");
};

const navigateBack = () => {
  router.push("/users");
};

const initializeForm = () => {
  ruleForm.email = store.getCurrentEditedUser.user_email;
  ruleForm.phone = store.getCurrentEditedUser.user_phone;
  ruleForm.name = store.getCurrentEditedUser.user_name;
  const ut = store.getCurrentEditedUser.user_type;
  ruleForm.user_type = ut === "Admin" || ut === 2 || ut === "2" ? "2" : "1";
};
initializeForm();
</script>

<template>
  <div class="fp-page">
    <div class="fp-inner">
      <div class="fp-card" v-loading="loading">
        <div class="fp-card-header">
          <div class="fp-card-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#2563eb"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
          </div>
          <div>
            <h1 class="fp-title">Edit a user</h1>
            <p class="fp-subtitle">
              Update this user's profile and access role
            </p>
          </div>
        </div>

        <div class="fp-error" v-if="formError">{{ formError }}</div>

        <el-form
          ref="ruleFormRef"
          :model="ruleForm"
          :rules="rules"
          label-width="0px"
          class="demo-ruleForm"
          :size="formSize"
          status-icon
        >
          <div class="fp-field">
            <label class="fp-label"
              >Name <span class="fp-required">*</span></label
            >
            <el-form-item prop="name" class="fp-form-item">
              <el-input
                v-model="ruleForm.name"
                placeholder="Enter the name of the user"
                size="large"
              />
            </el-form-item>
          </div>

          <div class="fp-field">
            <label class="fp-label"
              >Email Address <span class="fp-required">*</span></label
            >
            <el-form-item prop="email" class="fp-form-item">
              <el-input
                v-model="ruleForm.email"
                placeholder="Enter the user email"
                size="large"
              />
            </el-form-item>
          </div>

          <div class="fp-field">
            <label class="fp-label"
              >Phone Number <span class="fp-required">*</span></label
            >
            <el-form-item prop="phone" class="fp-form-item">
              <el-tooltip
                content="Only the users can change their phone numbers"
                placement="top"
              >
                <el-input
                  v-model="ruleForm.phone"
                  placeholder="Enter the user phone number"
                  size="large"
                  disabled
                />
              </el-tooltip>
            </el-form-item>
          </div>

          <div class="fp-divider"></div>

          <div class="fp-field">
            <label class="fp-label"
              >Role <span class="fp-required">*</span></label
            >
            <el-form-item prop="user_type" class="fp-form-item">
              <el-select
                v-model="ruleForm.user_type"
                placeholder="please select the user type"
                size="large"
                :disabled="!isCurrentUserAdmin"
              >
                <el-option label="Admin" value="2" />
                <el-option label="Normal" value="1" />
              </el-select>
            </el-form-item>
          </div>

          <button
            class="fp-submit-btn"
            @click.prevent="submitForm(ruleFormRef)"
            :disabled="loading"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"
              />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
            {{ loading ? "Updating..." : "Update User" }}
          </button>

          <button class="fp-back-btn" @click.prevent="navigateBack()">
            ← Back to Users
          </button>
        </el-form>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap");

* {
  box-sizing: border-box;
}

.fp-page {
  font-family: "DM Sans", sans-serif;
  background: #f8fafc;
  min-height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 48px 24px;
}

.fp-inner {
  width: 100%;
  max-width: 480px;
}

.fp-card {
  background: white;
  border-radius: 16px;
  border: 1.5px solid #e2e8f0;
  padding: 32px;
  box-shadow: 0 4px 24px rgba(18, 40, 74, 0.07);
}

.fp-card-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 28px;
}

.fp-card-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.fp-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #12284a;
  margin: 0 0 3px;
}

.fp-subtitle {
  font-size: 0.8rem;
  color: #94a3b8;
  margin: 0;
}

.fp-error {
  background: #fef2f2;
  border: 1.5px solid #fca5a5;
  color: #dc2626;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.82rem;
  font-weight: 500;
  margin-bottom: 20px;
}

.fp-divider {
  height: 1px;
  background: #f1f5f9;
  margin: 4px -32px 18px;
}

.fp-field {
  margin-bottom: 16px;
}

.fp-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
}

.fp-required {
  color: #dc2626;
}

.fp-form-item {
  margin-bottom: 0 !important;
}

:deep(.fp-form-item .ep-form-item__content),
:deep(.fp-form-item .el-form-item__content) {
  display: block;
  width: 100%;
}

:deep(.fp-form-item .ep-form-item__error),
:deep(.fp-form-item .el-form-item__error) {
  padding-top: 4px;
  font-size: 0.74rem;
}

:deep(.fp-form-item .el-input__wrapper) {
  box-shadow: 0 0 0 1.5px #e2e8f0 inset;
  border-radius: 9px;
  padding: 0 14px;
  min-height: 42px;
  background: white;
  transition: box-shadow 0.15s;
}

:deep(.fp-form-item .el-input__wrapper.is-focus) {
  box-shadow:
    0 0 0 1.5px #12284a inset,
    0 0 0 3px rgba(18, 40, 74, 0.08);
}

:deep(.fp-form-item .el-input.is-disabled .el-input__wrapper) {
  background: #f1f5f9;
}

:deep(.fp-form-item .el-select) {
  width: 100%;
}

:deep(.fp-form-item .el-select .el-select__wrapper) {
  box-shadow: 0 0 0 1.5px #e2e8f0 inset;
  border-radius: 9px;
  min-height: 42px;
  padding: 0 14px;
}

:deep(.fp-form-item .el-select .el-select__wrapper.is-focused) {
  box-shadow:
    0 0 0 1.5px #12284a inset,
    0 0 0 3px rgba(18, 40, 74, 0.08);
}

.fp-submit-btn {
  width: 100%;
  height: 44px;
  margin-top: 10px;
  background: linear-gradient(135deg, #12284a 0%, #1e3f73 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.15s;
}

.fp-submit-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.fp-submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.fp-back-btn {
  width: 100%;
  height: 38px;
  margin-top: 8px;
  background: none;
  border: none;
  color: #64748b;
  font-family: "DM Sans", sans-serif;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s;
}

.fp-back-btn:hover {
  color: #12284a;
}
</style>
